/* =========================================
   WAMBOLT BLOG INDEX
   ========================================= */

(function () {
  const ROOT_SELECTOR =
    '[data-blog-index]';

  const RESULTS_EXIT_DURATION =
    180;

  const REDUCED_MOTION_QUERY =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  const instances =
    new Map();


  /* =========================================
     SECTION URL
     ========================================= */

  function buildSectionUrl(
    sourceUrl,
    sectionId
  ) {
    const url =
      new URL(
        sourceUrl,
        window.location.origin
      );

    url.searchParams.set(
      'section_id',
      sectionId
    );

    return (
      url.pathname +
      url.search
    );
  }


  /* =========================================
     SEARCH QUERY
     ========================================= */

  function quoteSearchValue(
    value
  ) {
    const escaped =
      String(value || '')
        .replace(
          /\\/g,
          '\\\\'
        )
        .replace(
          /"/g,
          '\\"'
        );

    return `"${escaped}"`;
  }


  /* =========================================
     WAIT
     ========================================= */

  function wait(
    duration
  ) {
    return new Promise(
      (resolve) => {
        window.setTimeout(
          resolve,
          duration
        );
      }
    );
  }


  /* =========================================
     COMPONENT LIFECYCLE
     ========================================= */

  function dispatchLifecycleEvent(
    target,
    eventName
  ) {
    if (!target) {
      return;
    }

    target.dispatchEvent(
      new CustomEvent(
        eventName,
        {
          bubbles: true,
        }
      )
    );
  }


  function unloadDynamicContent(
    target
  ) {
    dispatchLifecycleEvent(
      target,
      'shopify:section:unload'
    );
  }


  function loadDynamicContent(
    target
  ) {
    window.requestAnimationFrame(
      () => {
        dispatchLifecycleEvent(
          target,
          'shopify:section:load'
        );
      }
    );
  }


  /* =========================================
     ROOT
     ========================================= */

  function initRoot(
    root
  ) {
    if (
      instances.has(root)
    ) {
      return;
    }


    const form =
      root.querySelector(
        '[data-blog-search-form]'
      );

    const input =
      root.querySelector(
        '[data-blog-search-input]'
      );

    const results =
      root.querySelector(
        '[data-blog-results]'
      );

    const status =
      root.querySelector(
        '[data-blog-status]'
      );

    const filterLinks =
      Array.from(
        root.querySelectorAll(
          '[data-blog-filter]'
        )
      );


    if (
      !form ||
      !input ||
      !results
    ) {
      return;
    }


    const sectionId =
      root.getAttribute(
        'data-blog-results-section'
      ) ||
      'wambolt-blog-results';


    const blogUrl =
      (
        root.getAttribute(
          'data-blog-url'
        ) ||
        ''
      ).replace(
        /\/$/,
        ''
      );


    let activeFilter =
      filterLinks.find(
        (link) =>
          link.getAttribute(
            'data-blog-filter-status'
          ) === 'active'
      ) ||
      filterLinks[0] ||
      null;


    let requestController =
      null;

    let debounceTimer =
      null;

    let requestVersion =
      0;


    /* =========================================
       CURRENT STATE
       ========================================= */

    function getQuery() {
      return input.value.trim();
    }


    function getActiveTagHandle() {
      return (
        activeFilter
          ?.getAttribute(
            'data-blog-filter-value'
          ) ||
        'all'
      );
    }


    function getActiveTagLabel() {
      return (
        activeFilter
          ?.getAttribute(
            'data-blog-filter-label'
          ) ||
        ''
      );
    }


    function getActiveFilterUrl() {
      return (
        activeFilter?.href ||
        blogUrl ||
        window.location.href
      );
    }


    /* =========================================
       STATUS
       ========================================= */

    function setStatus(
      message
    ) {
      if (!status) {
        return;
      }

      status.textContent =
        message || '';
    }


    function setLoading(
      isLoading
    ) {
      results.setAttribute(
        'aria-busy',
        isLoading
          ? 'true'
          : 'false'
      );
    }


    /* =========================================
       ACTIVE FILTER
       ========================================= */

    function setActiveFilter(
      nextFilter
    ) {
      activeFilter =
        nextFilter;


      filterLinks.forEach(
        (link) => {
          const isActive =
            link ===
            activeFilter;


          link.setAttribute(
            'data-blog-filter-status',
            isActive
              ? 'active'
              : 'not-active'
          );


          if (isActive) {
            link.setAttribute(
              'aria-current',
              'true'
            );
          } else {
            link.removeAttribute(
              'aria-current'
            );
          }
        }
      );
    }


    /* =========================================
       RESULT HELPERS
       ========================================= */

    function removeOtherBlogResults() {
      if (!blogUrl) {
        return;
      }


      const expectedPrefix =
        `${blogUrl}/`;


      results
        .querySelectorAll(
          '[data-blog-article]'
        )
        .forEach(
          (article) => {
            const articleUrl =
              article.getAttribute(
                'data-article-url'
              ) ||
              '';


            if (
              !articleUrl.startsWith(
                expectedPrefix
              )
            ) {
              article.remove();
            }
          }
        );
    }


    function countArticles() {
      return results
        .querySelectorAll(
          '[data-blog-article]'
        )
        .length;
    }


    function parseResponse(
      html
    ) {
      const responseDocument =
        new DOMParser()
          .parseFromString(
            html,
            'text/html'
          );


      return responseDocument
        .querySelector(
          '[data-blog-results-response]'
        );
    }


    /* =========================================
       RESULT MOTION
       ========================================= */

    async function animateResultsOut() {
      if (
        REDUCED_MOTION_QUERY.matches
      ) {
        return;
      }


      results.setAttribute(
        'data-blog-results-state',
        'exiting'
      );


      await wait(
        RESULTS_EXIT_DURATION
      );
    }


    function animateResultsIn() {
      if (
        REDUCED_MOTION_QUERY.matches
      ) {
        results.setAttribute(
          'data-blog-results-state',
          'ready'
        );

        return;
      }


      results.setAttribute(
        'data-blog-results-state',
        'entering'
      );


      /*
       * Two frames ensure the browser paints
       * the entering position before moving
       * the new cards into their ready state.
       */
      window.requestAnimationFrame(
        () => {
          window.requestAnimationFrame(
            () => {
              results.setAttribute(
                'data-blog-results-state',
                'ready'
              );
            }
          );
        }
      );
    }


    /* =========================================
       LAYOUT REFRESH
       ========================================= */

    function refreshLayout() {
      if (
        window.ScrollTrigger
          ?.sort
      ) {
        window.ScrollTrigger
          .sort();
      }


      if (
        window.ScrollTrigger
          ?.refresh
      ) {
        window.requestAnimationFrame(
          () => {
            window.ScrollTrigger
              .refresh();
          }
        );
      }


      if (
        window.wamboltLenis &&
        typeof
          window.wamboltLenis
            .resize ===
          'function'
      ) {
        window.wamboltLenis
          .resize();
      }
    }


    /* =========================================
       REPLACE RESULTS
       ========================================= */

    function replaceResults(
      incoming,
      options = {}
    ) {
      /*
       * Allow shared Wambolt components inside
       * the old cards to clean themselves up.
       */
      unloadDynamicContent(
        results
      );


      results.innerHTML =
        incoming.innerHTML;


      if (
        options.searchMode
      ) {
        removeOtherBlogResults();
      }


      /*
       * Initialize the new Action Buttons and
       * other shared Wambolt components.
       */
      loadDynamicContent(
        results
      );


      animateResultsIn();


      refreshLayout();
    }


    /* =========================================
       LOAD RESULTS
       ========================================= */

    async function loadResults(
      sourceUrl,
      options = {}
    ) {
      if (
        requestController
      ) {
        requestController.abort();
      }


      requestController =
        new AbortController();


      const currentVersion =
        ++requestVersion;


      const query =
        getQuery();


      setLoading(true);


      if (
        options.searchMode
      ) {
        setStatus(
          'Searching articles...'
        );
      } else {
        setStatus('');
      }


      try {

        /*
         * Fetch first.
         *
         * We intentionally leave the existing
         * articles visible while waiting for
         * Shopify so a slower response does not
         * create an empty screen.
         */
        const response =
          await fetch(
            buildSectionUrl(
              sourceUrl,
              sectionId
            ),
            {
              signal:
                requestController
                  .signal,

              headers: {
                'X-Requested-With':
                  'XMLHttpRequest',
              },
            }
          );


        if (!response.ok) {
          throw new Error(
            `Request failed: ${
              response.status
            }`
          );
        }


        const html =
          await response.text();


        if (
          currentVersion !==
          requestVersion
        ) {
          return;
        }


        const incoming =
          parseResponse(html);


        if (!incoming) {
          throw new Error(
            'Blog results section was not found.'
          );
        }


        /*
         * Once the new result set is ready,
         * animate the current cards out.
         */
        await animateResultsOut();


        if (
          currentVersion !==
          requestVersion
        ) {
          return;
        }


        replaceResults(
          incoming,
          {
            searchMode:
              Boolean(
                options.searchMode
              ),
          }
        );


        const resultCount =
          countArticles();


        if (
          resultCount === 0
        ) {

          if (query) {

            setStatus(
              `No articles found for "${query}".`
            );

          } else if (
            getActiveTagHandle() !==
            'all'
          ) {

            setStatus(
              'No articles were found in this category.'
            );

          } else {

            setStatus(
              'No articles have been published yet.'
            );

          }

        } else {

          setStatus('');

        }

      } catch (error) {

        if (
          error.name ===
          'AbortError'
        ) {
          return;
        }


        console.error(
          '[Wambolt Blog]',
          error
        );


        results.setAttribute(
          'data-blog-results-state',
          'ready'
        );


        setStatus(
          'Articles could not be loaded. Please try again.'
        );

      } finally {

        if (
          currentVersion ===
          requestVersion
        ) {
          setLoading(false);
        }

      }
    }


    /* =========================================
       SEARCH URL
       ========================================= */

    function buildSearchUrl() {
      const query =
        getQuery();


      const url =
        new URL(
          window.Shopify
            ?.routes
            ?.root
            ? `${window.Shopify.routes.root}search`
            : '/search',
          window.location.origin
        );


      let searchQuery =
        query;


      if (
        getActiveTagHandle() !==
        'all'
      ) {
        const tagLabel =
          getActiveTagLabel();


        if (tagLabel) {
          searchQuery =
            [
              searchQuery,
              `tag:${quoteSearchValue(
                tagLabel
              )}`,
            ]
              .filter(Boolean)
              .join(' ');
        }
      }


      url.searchParams.set(
        'q',
        searchQuery
      );


      url.searchParams.set(
        'type',
        'article'
      );


      url.searchParams.set(
        'options[prefix]',
        'last'
      );


      return url.toString();
    }


    /* =========================================
       CURRENT RESULT STATE
       ========================================= */

    function runCurrentState() {
      const query =
        getQuery();


      if (query) {

        loadResults(
          buildSearchUrl(),
          {
            searchMode: true,
          }
        );


        return;
      }


      loadResults(
        getActiveFilterUrl(),
        {
          searchMode: false,
        }
      );
    }


    /* =========================================
       SEARCH EVENTS
       ========================================= */

    function onInput() {
      window.clearTimeout(
        debounceTimer
      );


      debounceTimer =
        window.setTimeout(
          runCurrentState,
          300
        );
    }


    function onSubmit(
      event
    ) {
      event.preventDefault();
      event.stopPropagation();


      window.clearTimeout(
        debounceTimer
      );


      runCurrentState();
    }


    /* =========================================
       FILTER EVENTS
       ========================================= */

    function onFilterClick(
      event
    ) {
      const filter =
        event.target.closest(
          '[data-blog-filter]'
        );


      if (
        !filter ||
        !root.contains(filter)
      ) {
        return;
      }


      event.preventDefault();
      event.stopPropagation();


      /*
       * The active-state change happens
       * immediately, giving the filter button
       * time to visually transition while the
       * new Shopify result set is fetched.
       */
      setActiveFilter(
        filter
      );


      runCurrentState();
    }


    /* =========================================
       PAGINATION EVENTS
       ========================================= */

    function onResultsClick(
      event
    ) {
      const paginationLink =
        event.target.closest(
          '[data-blog-pagination] a'
        );


      if (
        !paginationLink ||
        !results.contains(
          paginationLink
        )
      ) {
        return;
      }


      event.preventDefault();
      event.stopPropagation();


      loadResults(
        paginationLink.href,
        {
          searchMode:
            Boolean(
              getQuery()
            ),
        }
      );
    }


    /* =========================================
       EVENTS
       ========================================= */

    form.addEventListener(
      'submit',
      onSubmit
    );


    input.addEventListener(
      'input',
      onInput
    );


    root.addEventListener(
      'click',
      onFilterClick
    );


    results.addEventListener(
      'click',
      onResultsClick
    );


    /* =========================================
       INSTANCE
       ========================================= */

    instances.set(
      root,
      {
        destroy() {

          window.clearTimeout(
            debounceTimer
          );


          requestVersion += 1;


          requestController
            ?.abort();


          form.removeEventListener(
            'submit',
            onSubmit
          );


          input.removeEventListener(
            'input',
            onInput
          );


          root.removeEventListener(
            'click',
            onFilterClick
          );


          results.removeEventListener(
            'click',
            onResultsClick
          );

        },
      }
    );
  }


  /* =========================================
     DESTROY ROOT
     ========================================= */

  function destroyRoot(
    root
  ) {
    const instance =
      instances.get(root);


    if (!instance) {
      return;
    }


    instance.destroy();


    instances.delete(root);
  }


  /* =========================================
     INITIALIZE
     ========================================= */

  function initAll(
    scope = document
  ) {
    if (
      scope.matches?.(
        ROOT_SELECTOR
      )
    ) {
      initRoot(scope);
    }


    scope
      .querySelectorAll?.(
        ROOT_SELECTOR
      )
      .forEach(
        initRoot
      );
  }


  /* =========================================
     DESTROY
     ========================================= */

  function destroyWithin(
    scope = document
  ) {
    Array.from(
      instances.keys()
    ).forEach(
      (root) => {

        if (
          scope === document ||
          scope === root ||
          scope.contains?.(
            root
          )
        ) {
          destroyRoot(root);
        }

      }
    );
  }


  /* =========================================
     BOOT
     ========================================= */

  function boot() {
    initAll(document);
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true,
      }
    );

  } else {

    boot();

  }


  /* =========================================
     SHOPIFY THEME EDITOR
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(
        event.target
      );
    }
  );


  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyWithin(
        event.target
      );
    }
  );


  /* =========================================
     BARBA
     ========================================= */

  document.addEventListener(
    'wambolt:page:before-leave',
    () => {
      destroyWithin(
        document
      );
    }
  );


  document.addEventListener(
    'wambolt:page:enter',
    () => {
      initAll(
        document
      );
    }
  );

})();